import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: authHeaders(),
    ...options,
  });
  if (!resp.ok) throw { message: `HTTP ${resp.status}` };
  return resp.json();
}

interface Equipment {
  id: string;
  code: string;
  name: string;
  type: string;
  location: string;
  status: 'operational' | 'maintenance' | 'breakdown' | 'idle';
  healthScore: number;
  lastMaintenance: string;
  nextMaintenance: string;
  utilizationRate: number;
  mtbf: number;
  mttr: number;
}

interface MaintenanceTask {
  id: string;
  taskNumber: string;
  equipmentId: string;
  equipmentName: string;
  type: 'preventive' | 'corrective' | 'predictive' | 'emergency';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'scheduled' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  description: string;
  assignedTo?: string;
  scheduledDate: string;
  completedDate?: string;
  estimatedDuration: number;
  actualDuration?: number;
  cost?: number;
  notes?: string;
}

interface MaintenanceAlert {
  id: string;
  equipmentId: string;
  equipmentName: string;
  alertType: 'overdue_maintenance' | 'health_degradation' | 'failure_prediction' | 'anomaly_detected';
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: string;
  acknowledged: boolean;
}

export interface CreateMaintenanceTaskDto {
  equipmentId: string;
  type: 'preventive' | 'corrective' | 'predictive' | 'emergency';
  priority: 'low' | 'medium' | 'high' | 'critical';
  scheduledDate: string;
  estimatedDuration: number;
  assignedTo?: string;
  description?: string;
}

export interface FailurePrediction {
  id: string;
  equipmentId: string;
  equipmentName: string;
  failureProbability: number;
  predictedDate: string;
  recommendedAction: string;
  status: string;
}

interface MaintenanceState {
  equipment: Equipment[];
  maintenanceTasks: MaintenanceTask[];
  alerts: MaintenanceAlert[];
  predictions: FailurePrediction[];
  selectedEquipment: Equipment | null;
  selectedTask: MaintenanceTask | null;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
}

const initialState: MaintenanceState = {
  equipment: [],
  maintenanceTasks: [],
  alerts: [],
  predictions: [],
  selectedEquipment: null,
  selectedTask: null,
  isLoading: false,
  isCreating: false,
  error: null,
};

export const fetchEquipment = createAsyncThunk(
  'maintenance/fetchEquipment',
  async () => {
    const data = await apiFetch('/predictive-maintenance/equipment/health-overview');
    const list = data.data ?? data;
    return Array.isArray(list) ? list : [];
  }
);

export const fetchMaintenanceTasks = createAsyncThunk(
  'maintenance/fetchTasks',
  async () => {
    const data = await apiFetch('/predictive-maintenance/schedules');
    const list = data.data ?? data;
    return Array.isArray(list) ? list : [];
  }
);

export const fetchMaintenanceAlerts = createAsyncThunk(
  'maintenance/fetchAlerts',
  async () => {
    const data = await apiFetch('/predictive-maintenance/predictions');
    const list = data.data ?? data;
    return Array.isArray(list) ? list : [];
  }
);

export interface UpdateTaskStatusPayload {
  id: string;
  status: MaintenanceTask['status'];
  completionNotes?: string;
  actualDuration?: number;
  completedBy?: string;
}

export const updateTaskStatus = createAsyncThunk(
  'maintenance/updateTaskStatus',
  async ({ id, status, completionNotes, actualDuration, completedBy }: UpdateTaskStatusPayload) => {
    await apiFetch(`/predictive-maintenance/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status, completionNotes, actualDuration, completedBy }),
    });
    return { id, status };
  }
);

export const acknowledgeAlert = createAsyncThunk(
  'maintenance/acknowledgeAlert',
  async (alertId: string) => {
    await apiFetch(`/predictive-maintenance/predictions/${alertId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'resolved' }),
    });
    return alertId;
  }
);

export const createMaintenanceTask = createAsyncThunk(
  'maintenance/createTask',
  async (data: CreateMaintenanceTaskDto, { rejectWithValue }) => {
    const resp = await fetch(`${API_BASE}/predictive-maintenance/schedules`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!resp.ok) return rejectWithValue(await resp.text());
    return resp.json();
  }
);

export const fetchFailurePredictions = createAsyncThunk(
  'maintenance/fetchPredictions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiFetch('/predictive-maintenance/predictions');
      const list = (data as any).data ?? data;
      return Array.isArray(list) ? list : [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch predictions');
    }
  }
);

const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedEquipment: (state, action: PayloadAction<Equipment | null>) => {
      state.selectedEquipment = action.payload;
    },
    setSelectedTask: (state, action: PayloadAction<MaintenanceTask | null>) => {
      state.selectedTask = action.payload;
    },
    updateEquipmentStatus: (state, action: PayloadAction<{ id: string; status: Equipment['status'] }>) => {
      const { id, status } = action.payload;
      const equipment = state.equipment.find(eq => eq.id === id);
      if (equipment) {
        equipment.status = status;
      }
    },
    updateEquipmentHealth: (state, action: PayloadAction<{ id: string; healthScore: number }>) => {
      const { id, healthScore } = action.payload;
      const equipment = state.equipment.find(eq => eq.id === id);
      if (equipment) {
        equipment.healthScore = healthScore;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEquipment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.equipment = action.payload;
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch equipment';
      })
      .addCase(fetchMaintenanceTasks.fulfilled, (state, action) => {
        state.maintenanceTasks = action.payload;
      })
      .addCase(fetchMaintenanceAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload;
      })
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const task = state.maintenanceTasks.find(task => task.id === id);
        if (task) {
          task.status = status;
        }
      })
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        const alertId = action.payload;
        const alert = state.alerts.find(alert => alert.id === alertId);
        if (alert) {
          alert.acknowledged = true;
        }
      })
      .addCase(createMaintenanceTask.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMaintenanceTask.fulfilled, (state) => {
        state.isCreating = false;
      })
      .addCase(createMaintenanceTask.rejected, (state, action) => {
        state.isCreating = false;
        state.error = (action.payload as string) || 'Failed to create maintenance task';
      })
      .addCase(fetchFailurePredictions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFailurePredictions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.predictions = action.payload;
      })
      .addCase(fetchFailurePredictions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch predictions';
      });
  },
});

export const {
  clearError,
  setSelectedEquipment,
  setSelectedTask,
  updateEquipmentStatus,
  updateEquipmentHealth,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
